import usePreOwnedCampaignData from '../data/usePreOwnedCampaignData';
import {
  IDefaultPreOwned,
  IDefaultPreOwnedCampaign,
  IPreOwned,
} from '../types';
import { parseData } from './parseData';

// Type Guard to check if the product is a default pre-owned product
const isDefaultPreOwned = (
  product: IPreOwned | IDefaultPreOwned
): product is IDefaultPreOwned => {
  return !('content' in product) || typeof product.content.product === 'string';
};

// Check if the campaign is active
const isCampaignActive = (campaign: IDefaultPreOwnedCampaign): boolean => {
  const currentDate = new Date();
  const startDate = campaign.content.start_date
    ? new Date(campaign.content.start_date)
    : null;
  const endDate = campaign.content.end_date
    ? new Date(campaign.content.end_date)
    : null;

  if (startDate && endDate) {
    return currentDate >= startDate && currentDate <= endDate;
  }
  if (startDate) {
    return currentDate >= startDate;
  }
  if (endDate) {
    return currentDate <= endDate;
  }

  return true;
};

// Get the pre-owned campaigns
export const getPreOwnedCampaigns = (product: IPreOwned | IDefaultPreOwned) => {
  const campaignData: IDefaultPreOwnedCampaign[] = parseData(
    usePreOwnedCampaignData()
  );

  const isValidCampaign = (
    campaign: IDefaultPreOwnedCampaign,
    uuidToCheck: string
  ) => {
    const notIncluded = campaign.content.not_included_boats || [];
    return !notIncluded.includes(uuidToCheck);
  };

  // Check if the specific product has a campaign, it takes precedence
  const specificProductCampaign = campaignData.find(
    (c) =>
      c.content.included_boats?.includes(product.uuid) &&
      isValidCampaign(c, product.uuid) &&
      isCampaignActive(c)
  );

  if (specificProductCampaign) {
    return specificProductCampaign;
  }

  // Else get the campaign for products
  const productUuid = isDefaultPreOwned(product)
    ? product.content.product
    : product.content.product?.uuid;

  const specificModelCampaign = campaignData.find(
    (c) =>
      c.content.included_products?.includes(productUuid) &&
      isValidCampaign(c, product.uuid) &&
      isCampaignActive(c)
  );

  if (specificModelCampaign) {
    return specificModelCampaign;
  }

  return undefined;
};
